/**
 * Publish Quiz Dialog
 *
 * Allows users to publish their custom quizzes to Supabase for sharing.
 * Generates a shareable URL and shows stats.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { toast } from 'sonner';
import { Copy, Check, Share2, ExternalLink } from 'lucide-react';
import type { QuizConfig } from '@/db/types';
import { slugify, generateShortId } from '@/utils/slugify';

interface PublishQuizDialogProps {
  isOpen: boolean;
  onClose: () => void;
  quizConfig: QuizConfig;
}

export function PublishQuizDialog({
  isOpen,
  onClose,
  quizConfig,
}: PublishQuizDialogProps) {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishedId, setPublishedId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [title, setTitle] = useState(quizConfig.title);
  const [description, setDescription] = useState(quizConfig.description || '');

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setTitle(quizConfig.title);
      setDescription(quizConfig.description || '');
      setPublished(false);
      setPublishedId(null);
      setCopied(false);
    }
  }, [isOpen, quizConfig]);

  // Check if not configured
  if (!isSupabaseConfigured) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publishing Unavailable</DialogTitle>
            <DialogDescription>
              Supabase is not configured. Cloud features are disabled.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={onClose}>Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Check if not authenticated
  if (!isAuthenticated) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign In Required</DialogTitle>
            <DialogDescription>
              You need to sign in to publish and share quizzes.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={() => window.location.href = '/auth'}>
              Sign In
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const handlePublish = async () => {
    if (!user) return;

    setLoading(true);

    try {
      // Generate slug and short ID for human-friendly URLs
      const slug = slugify(title);
      const shortId = generateShortId();

      // Prepare quiz data for publishing
      const sharedQuiz = {
        user_id: user.id,
        slug,
        short_id: shortId,
        title,
        description: description || null,
        category: quizConfig.category,
        dataset_label: quizConfig.datasetLabel,
        dataset_source: quizConfig.datasetSource,
        stored_collection: quizConfig.storedCollection,
        prompt_keys: quizConfig.promptKeys,
        filters: quizConfig.filters,
        excluded_feature_ids: quizConfig.excludedFeatureIds,
        scope_key: quizConfig.scopeKey,
        scope_bias: quizConfig.scopeBias,
        repetitions: quizConfig.repetitions,
        max_attempts_per_prompt: quizConfig.maxAttemptsPerPrompt,
        eligible_count: quizConfig.eligibleCount,
        total_features: quizConfig.totalFeatures,
        granularity_level: quizConfig.granularityLevel,
      };

      // Insert into Supabase
      const { error } = await supabase
        .from('shared_quizzes')
        .insert(sharedQuiz as any) // Type assertion due to JSONB field complexity
        .select()
        .single();

      if (error) throw error;

      // Store the shareable slug (slug-shortId) for the URL
      setPublishedId(`${slug}-${shortId}`);
      setPublished(true);

      toast.success('Quiz published!', {
        description: 'Your quiz is now available for others to play.',
      });
    } catch (error) {
      console.error('Failed to publish quiz:', error);
      toast.error('Failed to publish quiz', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = publishedId
    ? `${window.location.origin}/quiz/shared/${publishedId}`
    : '';

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        {!published ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Publish Quiz
              </DialogTitle>
              <DialogDescription>
                Share your custom quiz with the MapQuizzr community
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="publish-title">Quiz Title</Label>
                <Input
                  id="publish-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your quiz a catchy title"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="publish-description">Description (Optional)</Label>
                <Textarea
                  id="publish-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what makes this quiz unique"
                  disabled={loading}
                  rows={3}
                />
              </div>

              <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Category:</span>
                  <span className="font-medium">{quizConfig.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Questions:</span>
                  <span className="font-medium">{quizConfig.eligibleCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Region:</span>
                  <span className="font-medium">{quizConfig.datasetLabel}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handlePublish}
                  disabled={loading || !title.trim()}
                  className="flex-1"
                >
                  {loading ? 'Publishing...' : 'Publish Quiz'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <Check className="w-5 h-5" />
                Quiz Published!
              </DialogTitle>
              <DialogDescription>
                Your quiz is now live and ready to share
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="p-4 bg-accent/50 rounded-lg border border-accent">
                <Label className="text-xs text-muted-foreground mb-2 block">
                  Shareable Link
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={shareUrl}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleCopyLink}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="text-sm text-muted-foreground space-y-2">
                <p>✨ Your quiz is now discoverable by other users</p>
                <p>📊 View count and engagement stats will appear soon</p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Done
                </Button>
                <Button
                  onClick={() => window.open(shareUrl, '_blank')}
                  className="flex-1"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Quiz
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
